import lenis from "../managers/lenis.js";
import { isAboveBreakpoint } from "../helpers/responsiveCheck.js";

// Drives the mobile header overlay, plus the Platform dropdown — which, unlike the
// overlay, is live at every width. Above $tm the overlay half runs no further than
// the (harmless) listener registration, since the hamburger is display:none and so
// can never be clicked or focused; the dropdown half is the desktop's only JS nav
// behaviour, and below $tm the same disclosure renders as an inline accordion.
class Nav {
    constructor(elem) {
        this.elem = elem;
        this.toggle = this.elem.querySelector(".header__toggle");
        this.panel = this.elem.querySelector(".header__nav");
        this.logo = this.elem.querySelector(".header__logo");
        this.links = Array.from(this.panel.querySelectorAll("a"));

        // Anchors only, deliberately: `this.links` also carries the close-the-overlay
        // click handler below, and a dropdown trigger must expand rather than dismiss
        // the panel it lives in. Keeping the trigger out of this list is what makes
        // that fall out for free instead of needing a guard in the handler.
        this.menus = Array.from(this.panel.querySelectorAll(".header__item--menu")).map((item) => ({
            item,
            trigger: item.querySelector(".header__menu-toggle"),
            // The collapsible panel specifically, not the whole <li>: mouseleave needs to
            // distinguish focus sitting on the always-visible trigger from focus inside the
            // part that display:none is about to remove.
            list: item.querySelector(".header__menu"),
        }));

        // Held as a live MediaQueryList, not a boolean: a pointer can be added or removed
        // mid-session (a tablet docked to a keyboard and trackpad), and `.matches` is read
        // at event time so it tracks that without a change listener.
        this.canHover = window.matchMedia("(hover: hover)");

        // Siblings of <header> in base.njk, so marking them inert cannot reach the
        // panel itself. The footer is absent on 404 (hideFooter), hence the filter.
        this.background = [document.querySelector("main"), document.querySelector(".footer")].filter(Boolean);

        this.isOpen = false;

        this.init();
    }

    init() {
        this.toggle.addEventListener("click", () => this.setOpen(!this.isOpen));

        // Covers the same-page "#platform" anchor, where no navigation happens and
        // the panel would otherwise sit over the section it just scrolled to.
        //
        // Order matters and is load-bearing: lenis.scrollTo() returns early while
        // stopped, and Lenis binds its anchor handler to window. Listening on the <a>
        // means this runs in the target phase and restarts Lenis before the event
        // bubbles up to it. Delegate this to document, or defer it behind a
        // transitionend, and the anchor scroll silently stops working.
        this.links.forEach((link) =>
            link.addEventListener("click", () => {
                this.setOpen(false);

                // Also collapse the dropdown the link belongs to. setOpen alone does not
                // reach it above $tm: the overlay is never open there, so setOpen returns
                // at its no-change guard and the rAF that calls closeMenus never runs. The
                // result was a panel left floating over the very section a same-page
                // "#platform" click had just scrolled to, until the pointer happened to
                // leave — the same problem the overlay close above exists to prevent.
                //
                // Deferred for the reason setOpen defers `inert`: display:none-ing the
                // anchor mid-dispatch can cancel the navigation the click exists to
                // perform, which matters once these children point at real pages.
                const owner = this.menus.find((menu) => menu.item.contains(link));

                if (owner) requestAnimationFrame(() => this.setMenuOpen(owner, false));
            }),
        );

        document.addEventListener("keydown", (event) => this.onKeydown(event));

        // Drops the inert set on close once the fade-out lands and visibility: hidden
        // takes over the job. Self-limiting on purpose: a permanently inert panel would
        // kill the desktop nav for anyone who opens the overlay, closes it, then widens
        // past $tm — and clearing it from the resize handler would mean a getComputedStyle
        // on every resize tick, which the isOpen short-circuit exists to avoid.
        this.panel.addEventListener("transitionend", (event) => {
            if (event.target === this.panel && !this.isOpen) this.panel.removeAttribute("inert");
        });

        // A resize or rotate past the breakpoint swaps the overlay for the inline
        // row and hides the toggle, leaving nothing to clear the scroll lock with.
        // Tracks which side of $tm we were on, because the dropdown reset below has to
        // fire on the *transition*, not on every tick.
        let wasAboveBreakpoint = isAboveBreakpoint();

        window.addEventListener("resize", () => {
            if (this.isOpen && isAboveBreakpoint()) this.setOpen(false);

            // An open dropdown is a floating panel above $tm and an inline accordion
            // below it. Crossing the seam mid-open leaves it rendered in a box it was
            // never positioned for, so reset to collapsed rather than reconciling.
            //
            // Gated on actually crossing: unconditionally, this collapsed the panel on
            // any resize event at all, so dragging a window edge or opening devtools
            // while staying on one side of the seam snapped an open dropdown shut with
            // no mode change to justify it.
            const nowAboveBreakpoint = isAboveBreakpoint();

            if (nowAboveBreakpoint !== wasAboveBreakpoint) {
                wasAboveBreakpoint = nowAboveBreakpoint;
                this.closeMenus();
            }
        });

        this.menus.forEach((menu) => this.initMenu(menu));

        // Only above $tm: the mobile accordion sits inside a full-screen overlay whose
        // own toggle already dismisses it, and a document-level close would fire on the
        // tap that opened it.
        document.addEventListener("click", (event) => {
            if (!isAboveBreakpoint()) return;

            this.menus.forEach((menu) => {
                if (!menu.item.contains(event.target)) this.setMenuOpen(menu, false);
            });
        });
    }

    initMenu(menu) {
        menu.trigger.addEventListener("click", (event) => {
            // A pointer click cannot toggle where hover is doing the opening, because the
            // click lands *after* mouseenter has already opened the panel — toggling would
            // shut it again on the way in, so a mouse user could never get it to stay open.
            // Those users close it by leaving, clicking outside, or Esc.
            //
            // `detail` is the discriminator: Enter/Space on a <button> dispatch a click with
            // detail 0, a real pointer click has detail >= 1. It is paired with the hover
            // media query rather than used alone, because a wide touch screen (a landscape
            // tablet above $tm) gets neither hover nor detail 0 — there the click is the
            // only way in and must keep toggling, or the dropdown is unopenable.
            const openedByHover = event.detail > 0 && this.canHover.matches && isAboveBreakpoint();

            this.setMenuOpen(menu, openedByHover ? true : !this.isMenuOpen(menu));
        });

        // Hover is desktop sugar layered over the click/keyboard path, never the only way
        // in — a hover-only dropdown is unreachable by touch and by keyboard. Gated on the
        // media query as well as the breakpoint: below $tm a tap also emits mouseenter,
        // which would race the click handler above and toggle twice.
        menu.item.addEventListener("mouseenter", () => {
            if (isAboveBreakpoint() && this.canHover.matches) this.setMenuOpen(menu, true);
        });

        menu.item.addEventListener("mouseleave", () => {
            if (!isAboveBreakpoint() || !this.canHover.matches) return;

            // A drifting pointer must not yank the panel out from under a keyboard user.
            // Someone can open Platform with Enter, Tab into a child link, then bump a
            // resting mouse off the item — and display:none would leave activeElement on
            // a box-less element, focus effectively nowhere. When focus is in the panel,
            // the focusout handler below owns closing, and it fires on the way out.
            //
            // Scoped to the panel, not the whole <li>. Guarding on the <li> also caught
            // focus resting on the trigger, which is where a click leaves it — so hover
            // never closed again after the first click, which is most of the time. The
            // trigger keeps its box through the collapse, so that case is safe to close.
            if (menu.list?.contains(document.activeElement)) return;

            this.setMenuOpen(menu, false);
        });

        // Tabbing out of the last item closes the panel behind you. focusout fires before
        // focus lands, so relatedTarget is the incoming element — null when focus leaves
        // the document entirely, which must not count as "moved outside" or switching
        // tabs would collapse it. Desktop only: inside the overlay the Tab trap keeps
        // focus in the panel, and collapsing mid-cycle would drop the accordion rows out
        // of the very list the trap is walking.
        menu.item.addEventListener("focusout", (event) => {
            if (!isAboveBreakpoint()) return;
            if (event.relatedTarget && !menu.item.contains(event.relatedTarget)) this.setMenuOpen(menu, false);
        });
    }

    isMenuOpen(menu) {
        return menu.item.classList.contains("is-open");
    }

    setMenuOpen(menu, open) {
        if (open === this.isMenuOpen(menu)) return;

        menu.item.classList.toggle("is-open", open);
        menu.trigger.setAttribute("aria-expanded", String(open));
    }

    closeMenus() {
        this.menus.forEach((menu) => this.setMenuOpen(menu, false));
    }

    setOpen(open) {
        if (open === this.isOpen) return;

        this.isOpen = open;

        document.body.classList.toggle("nav-open", open);
        this.toggle.setAttribute("aria-expanded", String(open));
        this.toggle.setAttribute("aria-label", open ? "Close menu" : "Open menu");

        // The Tab trap only covers keyboard. Without this a screen reader's virtual
        // cursor or swipe navigation walks straight out of the panel into the page
        // behind it, which is still fully in the accessibility tree.
        this.background.forEach((region) => region.toggleAttribute("inert", open));

        // Closing does not hide the panel for another 400ms — the visibility transition
        // holds it at `visible` through the fade — so until then its links stay in the
        // tab order. Esc restores focus to the toggle, and one Tab from there would drop
        // straight into a nav the user cannot see. CSS pointer-events covers the pointer
        // path; this covers keyboard and AT, which pointer-events cannot.
        //
        // Both guards below are load-bearing. The rAF is because this method also runs
        // from a link's own click handler, and marking an ancestor inert mid-dispatch
        // cancels the navigation that click exists to perform. The breakpoint check is
        // because the resize force-close lands above $tm, where the panel is the plain
        // inline row with no fade-out — so no transitionend would ever fire to take the
        // attribute off again, leaving the desktop nav permanently unclickable.
        if (open) {
            this.panel.removeAttribute("inert");
        } else {
            requestAnimationFrame(() => {
                if (!this.isOpen && !isAboveBreakpoint()) this.panel.setAttribute("inert", "");

                // Collapsed here rather than synchronously above, for the same reason the
                // inert call is deferred: this method also runs from a submenu link's own
                // click handler, and display:none-ing the very anchor being dispatched can
                // cancel the navigation that click exists to perform. Reopening the overlay
                // should start collapsed — the accordion's rows are counted against the
                // landscape height budget in _header.scss only once expanded.
                this.closeMenus();
            });
        }

        // This is the scroll lock, not a nicety: stopping Lenis flips `lenis-stopped`
        // on <html>, which lenis.css turns into `overflow: clip` on the root. The
        // body.nav-open overflow is a fallback that never reaches the viewport.
        if (open) {
            lenis.stop();
        } else {
            lenis.start();
        }
    }

    // Recomputed per keypress rather than cached in the constructor, because the set is
    // no longer static: an accordion's links are display:none while collapsed, and a trap
    // that walked them would hand focus to an element with no box. getClientRects() is the
    // same visibility test verify-viewports.mjs applies.
    //
    // Note "a, button", not "a": the dropdown trigger is a <button>, which `this.links`
    // deliberately excludes (see the constructor). Leave it out here too and Tab lands on
    // the trigger, finds it absent from the cycle, and the next Tab walks straight out of
    // the overlay into the page behind — which is inert, so focus would vanish entirely.
    focusable() {
        const inPanel = Array.from(this.panel.querySelectorAll("a, button")).filter(
            (el) => el.getClientRects().length > 0,
        );

        // DOM order. The logo leads: _header.scss raises it above the panel so it
        // stays visible while open, which makes it a working home link for pointer
        // users — leaving it out gave keyboard users no way to reach it.
        return [this.logo, this.toggle, ...inPanel];
    }

    onKeydown(event) {
        // Ahead of the overlay guard below, and that ordering is the whole point: above $tm
        // `this.isOpen` is always false, so folding this in after it would leave Esc dead on
        // desktop — the one width where the dropdown is the only thing open.
        if (event.key === "Escape") {
            const openMenu = this.menus.find((menu) => this.isMenuOpen(menu));

            if (openMenu) {
                this.setMenuOpen(openMenu, false);
                openMenu.trigger.focus();
                return;
            }
        }

        if (!this.isOpen) return;

        if (event.key === "Escape") {
            this.setOpen(false);
            this.toggle.focus();
            return;
        }

        if (event.key !== "Tab") return;

        const focusable = this.focusable();
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (!focusable.includes(active)) {
            event.preventDefault();
            first.focus();
        } else if (event.shiftKey && active === first) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && active === last) {
            event.preventDefault();
            first.focus();
        }
    }
}

const headerElem = document.querySelector(".header");
if (headerElem?.querySelector(".header__toggle")) new Nav(headerElem);
